import { createContext, useCallback, useContext, useEffect, useRef, useState, type ReactNode } from "react";
import type { PublishStatusResponse } from "@yushi-portfolio/api-types";
import type { ApiClient } from "./api";

type PublishingState = { status?: PublishStatusResponse; refresh(): Promise<void> };
const PublishingContext = createContext<PublishingState | undefined>(undefined);
const POLL_MS = 10_000;

/** Site build status, polled while the public site is behind the latest publish. */
export function PublishingProvider({ api, children }: { api: ApiClient; children: ReactNode }) {
  const [status, setStatus] = useState<PublishStatusResponse>();
  const apiRef = useRef(api);
  const refresh = useCallback(async () => {
    try {
      setStatus(await apiRef.current.publishStatus());
    } catch {
      // Status is informational; editing keeps working without it.
    }
  }, []);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  const building = status?.pending || status?.lastBuild?.status === "IN_PROGRESS";
  useEffect(() => {
    if (!building) return;
    const timer = window.setInterval(() => void refresh(), POLL_MS);
    return () => window.clearInterval(timer);
  }, [building, refresh]);

  return <PublishingContext.Provider value={{ status, refresh }}>{children}</PublishingContext.Provider>;
}

export function usePublishing(): PublishingState {
  const value = useContext(PublishingContext);
  if (!value) throw new Error("usePublishing outside PublishingProvider");
  return value;
}

export function SiteStatus() {
  const { status } = usePublishing();
  if (!status) return null;
  const failed = status.lastBuild && ["FAILED", "FAULT", "TIMED_OUT", "STOPPED"].includes(status.lastBuild.status);
  if (status.pending || status.lastBuild?.status === "IN_PROGRESS") return <span className="badge warn">サイト更新中…</span>;
  if (failed) return <span className="badge error-badge" title={status.lastBuild?.id}>サイト更新に失敗</span>;
  return <span className="badge published">サイトは最新</span>;
}
